import { useLayoutEffect, useRef } from "react";
import { Box, Container } from "@mui/material";
import { styled } from "@mui/material/styles";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import {
  HeroSection,
  MissionStatement,
  FeaturesGrid,
  HowItWorks,
  GetStarted,
  TestimonialsSlider,
  CTASection,
} from "../components";

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const LandingPageContainer = styled(Box)(() => ({
  backgroundColor: "var(--background-page)",
  minHeight: "100vh",
}));

const MainContentContainer = styled(Container)(({ theme }) => ({
  width: "calc(100% - 24px)",
  maxWidth: "1200px",
  marginTop: theme.spacing(8),
  display: "flex",
  flexDirection: "column",
  gap: "150px",
  paddingBottom: theme.spacing(18.75),
  [theme.breakpoints.down("md")]: {
    gap: "80px",
    paddingBottom: theme.spacing(0),
  },
}));

// Horizontal scroll container wrapper
const HorizontalOuter = styled(Box)(({ theme }) => ({
  overflow: "hidden",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    margin: "80px 0",
  },
  [theme.breakpoints.down("lg")]: {
    overflow: "visible",
  },
}));

// Horizontal scroll container
const HorizontalContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "100vh",
  display: "flex",
  flexWrap: "nowrap",
  [theme.breakpoints.down("lg")]: {
    flexDirection: "column",
    height: "auto",
  },
}));

// Vertical sections after horizontal scroll
const VerticalSection = styled(Container)(({ theme }) => ({
  width: "calc(100% - 24px)",
  maxWidth: "1200px",
  margin: "0 auto",
  paddingBottom: theme.spacing(18.75),
  [theme.breakpoints.down("md")]: {
    paddingBottom: theme.spacing(10),
  },
}));

export default function LandingPage() {
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    // Check if we're on desktop (lg breakpoint and above)
    const mediaQuery = window.matchMedia("(min-width: 900px)");
    
    // Only initialize GSAP on desktop
    if (!mediaQuery.matches) {
      return; // Exit early on mobile - no GSAP initialization
    }

    // Get all horizontal panels
    const panels = gsap.utils.toArray<HTMLDivElement>(".panel-horizontal");
    const container = containerRef.current;

    if (!container || panels.length === 0) return;

    // Create GSAP context for cleanup
    const ctx = gsap.context(() => {
      // Calculate dimensions
      const panelWidth = window.innerWidth * 0.85;
      const gap = 20;
      const totalWidth = (panelWidth * panels.length) + (gap * (panels.length - 1));
      
      // Calculate centering offset for first card
      const centerOffset = (window.innerWidth - panelWidth) / 2;
      
      container.style.width = `${totalWidth + (centerOffset * 2)}px`;
      container.style.paddingLeft = `${centerOffset}px`;
      container.style.paddingRight = `${centerOffset}px`;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: ".horizontal-outer",
          pin: true,
          scrub: 1,
          snap: 1 / (panels.length - 1),
          end: () => `+=${totalWidth}`,
          anticipatePin: 1,
        },
      });

      // Animate horizontal movement
      tl.to(panels, {
        xPercent: -100 * (panels.length - 1),
        ease: "none",
      });
    });

    // Handle window resize - only on desktop
    const handleResize = () => {
      const isDesktop = window.matchMedia("(min-width: 900px)").matches;
      if (isDesktop) {
        ScrollTrigger.refresh();
      }
    };

    window.addEventListener("resize", handleResize);

    // Cleanup on unmount
    return () => {
      ctx.revert();
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <LandingPageContainer>
      <HeroSection />
      <MainContentContainer disableGutters>
        <MissionStatement />
        <FeaturesGrid />
        <HowItWorks />
        <GetStarted />
      </MainContentContainer>

      {/* Horizontal scroll section for testimonials */}
      <HorizontalOuter className="horizontal-outer">
        <HorizontalContainer ref={containerRef}>
          <TestimonialsSlider />
        </HorizontalContainer>
      </HorizontalOuter>

      {/* Vertical sections after horizontal scroll */}
      <VerticalSection disableGutters>
        <CTASection />
      </VerticalSection>
    </LandingPageContainer>
  );
}
